import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, MessagesSquare, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_OPTIONS = [
  "Mathematics Core",
  "Mathematical Literacy",
  "Science",
  "CAD (Computer-Aided Draughting)",
  "Not sure yet",
];

const GRADE_OPTIONS = ["Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"];

const Field = ({ id, label, required, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="font-heading text-sm font-bold text-slate-800">
      {label} {required && <span className="text-amber-600">*</span>}
    </Label>
    {children}
  </div>
);

const SuccessPanel = ({ title, text, testId, onReset }) => (
  <div className="flex flex-col items-center rounded-2xl border border-green-200 bg-green-50 px-6 py-12 text-center" data-testid={testId}>
    <CheckCircle2 className="h-12 w-12 text-green-600" />
    <h3 className="mt-4 font-heading text-xl font-bold text-slate-900">{title}</h3>
    <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">{text}</p>
    <Button variant="outline" className="mt-6 rounded-full font-heading font-bold" onClick={onReset} data-testid={`${testId}-another`}>
      Send another request
    </Button>
  </div>
);

const BookingForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", grade: "", mode: "",
    preferred_date: "", preferred_time: "", notes: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the privacy consent to continue.");
    setBusy(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, notes: form.notes || null });
      setDone(true);
      toast.success("Lesson request received — we'll confirm your slot personally.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again or email us.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <SuccessPanel
        testId="booking-success"
        title="Lesson request received."
        text="Thank you — BSA will contact you to confirm the slot, tutor and arrangements. Nothing is final until we've spoken to you."
        onReset={() => { setDone(false); setForm({ name: "", email: "", phone: "", subject: "", grade: "", mode: "", preferred_date: "", preferred_time: "", notes: "", consent: false }); }}
      />
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="b-name" label="Parent / student name" required>
          <Input id="b-name" data-testid="booking-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" />
        </Field>
        <Field id="b-phone" label="Phone / WhatsApp number" required>
          <Input id="b-phone" data-testid="booking-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="e.g. 082 000 0000" />
        </Field>
      </div>
      <Field id="b-email" label="Email address" required>
        <Input id="b-email" data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.co.za" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="b-subject" label="Subject" required>
          <Select required value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
            <SelectTrigger id="b-subject" data-testid="booking-subject-select"><SelectValue placeholder="Choose a subject" /></SelectTrigger>
            <SelectContent>
              {SUBJECT_OPTIONS.filter((s) => s !== "Not sure yet").map((s) => (
                <SelectItem key={s} value={s} data-testid={`booking-subject-${s.split(" ")[0].toLowerCase()}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="b-grade" label="Grade" required>
          <Select required value={form.grade} onValueChange={(v) => setForm((f) => ({ ...f, grade: v }))}>
            <SelectTrigger id="b-grade" data-testid="booking-grade-select"><SelectValue placeholder="Choose a grade" /></SelectTrigger>
            <SelectContent>
              {GRADE_OPTIONS.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field id="b-mode" label="Online or in person?" required>
        <div className="grid grid-cols-2 gap-3" data-testid="booking-mode-group">
          {[
            { v: "online", label: "Online lesson" },
            { v: "in-person", label: "In person — Witbank" },
          ].map(({ v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => setForm((f) => ({ ...f, mode: v }))}
              data-testid={`booking-mode-${v}`}
              className={`rounded-xl border-2 px-4 py-3 font-heading text-sm font-bold transition-colors duration-200 ${
                form.mode === v ? "border-primary bg-primary/5 text-primary" : "border-border bg-white text-muted-foreground hover:border-secondary/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="b-date" label="Preferred date" required>
          <Input id="b-date" data-testid="booking-date-input" type="date" required value={form.preferred_date} onChange={set("preferred_date")} />
        </Field>
        <Field id="b-time" label="Preferred time" required>
          <Select required value={form.preferred_time} onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}>
            <SelectTrigger id="b-time" data-testid="booking-time-select"><SelectValue placeholder="Choose a time" /></SelectTrigger>
            <SelectContent>
              {["After school (14:00–16:00)", "Late afternoon (16:00–18:00)", "Evening (18:00–20:00)", "Saturday morning"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field id="b-notes" label="Anything we should know?">
        <Textarea id="b-notes" data-testid="booking-notes-input" rows={3} value={form.notes} onChange={set("notes")} placeholder="Current marks, topics your child finds hard, goals…" />
      </Field>
      <div className="flex items-start gap-2.5">
        <Checkbox id="b-consent" data-testid="booking-consent-checkbox" checked={form.consent} onCheckedChange={(c) => setForm((f) => ({ ...f, consent: !!c }))} />
        <Label htmlFor="b-consent" className="text-xs leading-relaxed text-muted-foreground">
          I agree that BSA may use this information to respond to my booking request. *
        </Label>
      </div>
      <Button type="submit" disabled={busy} className="w-full rounded-full bg-primary py-6 font-heading text-base font-bold hover:bg-[#082A45]" data-testid="booking-submit-btn">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarCheck className="mr-2 h-4 w-4" />}
        Request this lesson slot
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        Every booking is confirmed personally before it is final. No payment is taken on this page.
      </p>
    </form>
  );
};

const ConsultationForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", enquiring_as: "", subject: "", grade: "", mode: "", message: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the privacy consent to continue.");
    if (!form.enquiring_as) return toast.error("Please tell us who is enquiring.");
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        enquiring_as: form.enquiring_as,
        subject: form.subject || null,
        grade: form.grade || null,
        mode: form.mode || null,
        message: form.message || null,
        consent: form.consent,
      });
      setDone(true);
      toast.success("Consultation request sent — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again or email us.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <SuccessPanel
        testId="consultation-success"
        title="Consultation request sent."
        text="Thank you for reaching out to BSA. We'll contact you to talk through your child's needs — no obligation, no payment."
        onReset={() => { setDone(false); setForm({ name: "", email: "", phone: "", enquiring_as: "", subject: "", grade: "", mode: "", message: "", consent: false }); }}
      />
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="consultation-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="c-name" label="Your name" required>
          <Input id="c-name" data-testid="consultation-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" />
        </Field>
        <Field id="c-email" label="Email address" required>
          <Input id="c-email" data-testid="consultation-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.co.za" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="c-phone" label="Phone number">
          <Input id="c-phone" data-testid="consultation-phone-input" value={form.phone} onChange={set("phone")} placeholder="Optional" />
        </Field>
        <Field id="c-as" label="I'm enquiring as" required>
          <Select value={form.enquiring_as} onValueChange={(v) => setForm((f) => ({ ...f, enquiring_as: v }))}>
            <SelectTrigger id="c-as" data-testid="consultation-role-select"><SelectValue placeholder="Select an option" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="parent" data-testid="consultation-role-parent">Parent or guardian</SelectItem>
              <SelectItem value="student" data-testid="consultation-role-student">Student</SelectItem>
              <SelectItem value="other" data-testid="consultation-role-other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-3">
        <Field id="c-subject" label="Subject">
          <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
            <SelectTrigger id="c-subject" data-testid="consultation-subject-select"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              {SUBJECT_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="c-grade" label="Grade">
          <Select value={form.grade} onValueChange={(v) => setForm((f) => ({ ...f, grade: v }))}>
            <SelectTrigger id="c-grade" data-testid="consultation-grade-select"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              {GRADE_OPTIONS.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="c-mode" label="Preference">
          <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
            <SelectTrigger id="c-mode" data-testid="consultation-mode-select"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="in-person">In person</SelectItem>
              <SelectItem value="not-sure">Not sure yet</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field id="c-message" label="Tell us a little more">
        <Textarea id="c-message" data-testid="consultation-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="How can we help your child?" />
      </Field>
      <div className="flex items-start gap-2.5">
        <Checkbox id="c-consent" data-testid="consultation-consent-checkbox" checked={form.consent} onCheckedChange={(c) => setForm((f) => ({ ...f, consent: !!c }))} />
        <Label htmlFor="c-consent" className="text-xs leading-relaxed text-muted-foreground">
          I agree that BSA may use this information to respond to my enquiry. *
        </Label>
      </div>
      <Button type="submit" disabled={busy} className="w-full rounded-full bg-secondary py-6 font-heading text-base font-bold text-white hover:bg-primary" data-testid="consultation-submit-btn">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessagesSquare className="mr-2 h-4 w-4" />}
        Request a free consultation
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        No payment or automatic registration takes place through this form.
      </p>
    </form>
  );
};

export const BookingSection = () => {
  return (
    <section id="book" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-testid="booking-section">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow-label" data-testid="booking-eyebrow">Booking &amp; consultation</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="booking-heading">
            Take the next step.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Book a lesson slot directly, or start with a free consultation if you'd like to talk it through first.
            Either way, a real person from BSA confirms everything with you.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              "Small groups and personal attention for every learner",
              "Online lessons anywhere in South Africa",
              "In person at 43 Benjamin Bennet Street, Duvhapark, Witbank",
              "Free, no-obligation consultation for parents",
            ].map((t, i) => (
              <li key={t} className="flex items-start gap-3" data-testid={`booking-benefit-${i}`}>
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                <span className="text-sm font-medium leading-relaxed text-slate-700 sm:text-base">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div id="consultation" className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-10">
            <Tabs defaultValue="book" data-testid="booking-tabs">
              <TabsList className="grid w-full grid-cols-2 rounded-full bg-muted p-1">
                <TabsTrigger value="book" className="rounded-full font-heading font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-book-lesson">
                  Book a lesson
                </TabsTrigger>
                <TabsTrigger value="consult" className="rounded-full font-heading font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-consultation">
                  Free consultation
                </TabsTrigger>
              </TabsList>
              <TabsContent value="book" className="mt-8">
                <BookingForm />
              </TabsContent>
              <TabsContent value="consult" className="mt-8">
                <ConsultationForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};
